/** Bandeja móvil: fetching con clave tenant-first (páginas sin HTTP directo). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import {
  tasksService,
  type WorkflowContext,
  type WorkflowMembershipLookup,
  type WorkflowTask,
  type WorkflowTaskDetail,
} from '@/services/tasksService';
import { useMobileAuth } from '@/state/MobileAuthContext';

export function useMobileTasksQuery(tab: 'open' | 'done') {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowTask[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-tasks', tab)
        : ['tenant', 'no-context', 'mobile-tasks', tab],
    queryFn: () =>
      tab === 'open'
        ? tasksService.mine({ status: 'open' })
        : tasksService.mine({ status_group: 'resolved' }),
    enabled: tenantId !== null && hasPermission('workflow_tasks.view_own'),
    staleTime: 30_000,
  });
}

/** Membresías elegibles para reasignar la tarea abierta. */
export function useMobileTaskMembershipsQuery(input: {
  entityCode: string | undefined;
  workflowPointCode: string | undefined;
  taskId: string | undefined;
  enabled: boolean;
}) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const { entityCode, workflowPointCode, taskId, enabled } = input;
  return useQuery<WorkflowMembershipLookup[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(
            tenantId,
            'mobile-task-memberships',
            entityCode,
            workflowPointCode,
            taskId,
          )
        : [
            'tenant',
            'no-context',
            'mobile-task-memberships',
            entityCode,
            workflowPointCode,
            taskId,
          ],
    queryFn: () =>
      tasksService.memberships({
        entityCode: entityCode!,
        workflowPointCode: workflowPointCode!,
        taskId: taskId!,
      }),
    enabled:
      enabled &&
      tenantId !== null &&
      !!entityCode &&
      !!workflowPointCode &&
      !!taskId &&
      hasPermission('workflow_instances.manage'),
    staleTime: 30_000,
  });
}

export function useMobileTaskQuery(taskId: string) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowTaskDetail>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-task', taskId)
        : ['tenant', 'no-context', 'mobile-task', taskId],
    queryFn: () => tasksService.get(taskId),
    enabled: tenantId !== null && taskId !== '' && hasPermission('workflow_tasks.view_own'),
    staleTime: 30_000,
  });
}

export function useMobileTaskContextQuery(entityCode: string | undefined, entityId: string | undefined) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowContext>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-task-context', entityCode, entityId)
        : ['tenant', 'no-context', 'mobile-task-context', entityCode, entityId],
    queryFn: () => tasksService.context(entityCode!, entityId!),
    enabled: tenantId !== null && entityCode !== undefined && entityId !== undefined,
    staleTime: 30_000,
  });
}

export function useDecideTaskMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: {
      taskId: string;
      decision: 'approve' | 'reject';
      reason: string | undefined;
      expectedTaskRevision: number;
    }) =>
      tasksService.decide(input.taskId, {
        decision: input.decision,
        reason: input.reason,
        expectedTaskRevision: input.expectedTaskRevision,
      }),
    onSuccess: (_data, variables) => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-tasks') });
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-task', variables.taskId),
        });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-task-context') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-document') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-quotations') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-budgets') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-requirements') });
      }
    },
  });
}
